// The invoices filed against one service record (round 31).
//
// Zanetto Tools' shape, ported for the reason its own migration gives: an
// invoice uploaded on a service record shows in BOTH places — here, beside the
// work it paid for, and in the machine's Service & repair invoices folder. It
// is one document, filed once.
//
// Deliberately small. This sits inside a list item in the service history, so
// it is a line of links and a file input, not a panel: a machine with twenty
// services would otherwise have twenty upload boxes stacked down the page.
import { useState } from 'react';
import { contentUrl, type Attachment } from '../documentsApi';
import { UploadBox } from './Documents';
import { DocumentViewer } from './DocumentViewer';
import { DownloadLink } from './DownloadLink';

export function ServiceInvoices({
  resourceId,
  serviceRecordId,
  invoices,
  onChanged,
  onError,
}: {
  resourceId: string;
  serviceRecordId: string;
  invoices: Attachment[];
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [attaching, setAttaching] = useState(false);
  const [viewing, setViewing] = useState<Attachment | null>(null);

  return (
    <div className="mt-2">
      {/* The invoices and the Attach control share ONE row. Stacked, the
          button sat a padding's width right of the titles above it and read
          as belonging to nothing — visible in the guide screenshot, which is
          where a 10px misalignment is easiest to see. */}
      {(invoices.length > 0 || !attaching) && (
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {invoices.map((invoice) => (
            <li key={invoice.id}>
              <button
                type="button"
                // 44px, like every other tap target — this one sits among
                // small grey metadata and would otherwise be a 16px link on a
                // phone.
                className="inline-flex min-h-11 items-center text-left text-brand-800"
                onClick={() => setViewing(invoice)}
              >
                {invoice.title}
              </button>
              <DownloadLink href={contentUrl(invoice.id, { download: true })} className="row-action ml-1">
                Download
              </DownloadLink>
            </li>
          ))}
          {/* The list stays visible while the box is open — somebody adding
              the second invoice for one repair needs to see the first. */}
          <li>
            {attaching ? (
              <button
                type="button"
                className="row-action"
                onClick={() => setAttaching(false)}
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="row-action"
                onClick={() => setAttaching(true)}
              >
                Attach invoice
              </button>
            )}
          </li>
        </ul>
      )}

      {attaching && (
        <UploadBox
          kind="resource"
          ownerId={resourceId}
          allowExpiry={false}
          photosOnly={false}
          serviceRecordId={serviceRecordId}
          hint="The invoice for this work. It files itself under Service & repair invoices."
          onUploaded={() => {
            setAttaching(false);
            onChanged();
          }}
          onError={onError}
        />
      )}

      {viewing && (
        <DocumentViewer attachment={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}
